import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { statisticsQueryKeys } from 'src/queries/queries.constants'
import { postStatistics } from 'src/queries/statistics/statistics.network'
import { STATISTICS_STALE_TIME_MS } from 'src/queries/statistics/statistics.queries'
import {
  StatisticAggregateType,
  StatisticModelType,
} from 'src/requests/statistics.request'

/** Budget id → number of ACTIVE virtual cards issued under it. */
export type BudgetVirtualCardCounts = Record<string, number>

/**
 * Active virtual-card counts for the budgets currently on screen (EX-40737),
 * backing the redesigned budget table's "Cards" column.
 *
 * ONE request for the whole page of budgets, not one per row. POST /statistics
 * takes a list of definitions and returns their values in request order, so a
 * page of ten budgets costs a single round trip — which is why this does its own
 * batching rather than going through `statisticsQueries.single`. That helper
 * sends one definition per request precisely so each fixed `StatisticsKeys` stat
 * gets an individually invalidatable cache entry; this stat is parameterised by
 * the ids on screen, so it has no such entry to share and per-row requests would
 * buy nothing but N-way fan-out.
 *
 * Counts ACTIVE cards only, matching the legacy
 * `getCreditCardsStatisticsActiveVcnCount` thunk the rest of the app reads its
 * per-card totals from (`all: true` included, so the count is not scoped to the
 * requesting user's own cards). A budget's cancelled cards are not what "Cards"
 * means on this table.
 */
export const budgetVirtualCardCountQueries = {
  byBudgetIds: (budgetIds: string[]) => {
    /*
      Sorted ONCE, and everything downstream uses this copy.

      The key is order-insensitive so two surfaces holding the same budgets hit
      one cache entry — Overview and the Budgets tab routinely list the same ids
      in different orders. That sharing is only safe if the REQUEST is built in
      the same order the key was derived from: the backend returns values in
      request order, so zipping a cached response against a different caller's
      ordering silently attributes each count to the wrong budget.
    */
    const orderedBudgetIds = [...budgetIds].sort()

    return queryOptions({
      queryKey:
        statisticsQueryKeys.budgetActiveVirtualCardCounts(orderedBudgetIds),
      /*
        Zipped here rather than in `select` for two reasons. `select` is
        observer-only, so a direct cache reader (`getQueryData`, `prefetchQuery`)
        would get the raw positional array instead of the map; and an inline
        `select` is a new reference every render, which defeats React Query's
        result reuse and re-mints the map each time (src/queries/AGENTS.md).
        Mapping in the queryFn puts a plain, correct object in the cache.
      */
      queryFn: async (): Promise<BudgetVirtualCardCounts> => {
        const data = await postStatistics(
          orderedBudgetIds.map((budgetId) => ({
            agg: StatisticAggregateType.Count,
            type: StatisticModelType.VirtualCard as const,
            query: {
              status: VirtualCardStatus.Active,
              creditCardId: budgetId,
              all: true,
            },
          })),
        )

        /*
          Built by mutating a local accumulator rather than spreading one into a
          new object per id. The spread form is O(n²), and n is not small or
          bounded here — the Budgets tab pages without limit, so this runs over
          every budget loaded so far on each fetch.

          Safe to mutate: `counts` is created here and never escapes until it is
          returned, so nothing observes a partially built object.
        */
        const counts: BudgetVirtualCardCounts = {}
        orderedBudgetIds.forEach((budgetId, index) => {
          const value = data.statistics[index]?.value
          // Left out entirely, not defaulted to 0 — a budget the response has
          // no value for has an UNKNOWN card count, which the table renders as
          // an em dash rather than claiming it has no cards.
          if (value !== undefined) {
            counts[budgetId] = value
          }
        })

        return counts
      },
      /*
        The key is the whole loaded id set, and the Budgets tab grows that set as
        it pages (10 → 20 → 30), so every page mints a NEW key. Without this the
        new key has no data while its fetch is in flight, the hook falls back to
        `{}`, and every already-rendered row's Cards value flips to an em dash
        and back on each page.

        Same reason and same remedy as the sibling `creditCardQueries.list`
        (`creditCard.queries.tsx`), which carries it with the note "re-fetches
        happen in place without resetting back to an empty state".

        NOT solved by this: page k still POSTs k·pageSize definitions, so the
        cumulative backend count work over a long scroll is O(k²). Keying per
        page chunk instead would fix that, at the cost of the cross-surface cache
        sharing the sorted whole-set key buys (Overview's 5 ids would no longer
        hit the Budgets tab's entry) and of chunk boundaries that shift whenever
        a budget is added or removed. Left as-is deliberately; the flicker was
        the user-visible half.
      */
      placeholderData: keepPreviousData,
      staleTime: STATISTICS_STALE_TIME_MS,
      // An empty page of budgets has nothing to count, and POSTing an empty
      // `statistics` array is a request whose answer is already known.
      enabled: orderedBudgetIds.length > 0,
    })
  },
}

/**
 * Convenience hook for {@link budgetVirtualCardCountQueries.byBudgetIds}.
 *
 * Returns an empty map — never undefined — while the request is in flight or
 * after it fails, so the table renders a missing count as an em dash rather than
 * withholding the whole row.
 */
export const useQueryBudgetVirtualCardCounts = (
  budgetIds: string[],
): BudgetVirtualCardCounts => {
  const { data } = useQuery(
    budgetVirtualCardCountQueries.byBudgetIds(budgetIds),
  )
  return data ?? {}
}
